// L'installation guidée (lot 6.1) : de l'inscription à la première notification, sans aide. Trois étapes, chacune dit
// le geste de CE téléphone et de CE navigateur : installer Alfred sur l'écran d'accueil (obligatoire sur iPhone pour
// les notifications), activer les notifications, en recevoir une d'essai. Proposé après l'inscription, puis par un
// bandeau du brief tant que ce n'est pas fait (BandeauInstallation).

import { type ReactNode, useEffect, useState } from 'react';

import type { Actions } from '../App';
import { EnTeteRetour, Fenetre } from '../components';
import { api } from '../lib/api';
import {
  bandeauAttendu, estIphone, gestesInstallation, installableDirectement, installerDirectement, lireSuivi, noterFait,
  noterPlusTard, plateformeCourante, surInvitation,
} from '../lib/installation';
import { type EtatAbonnement, activer, empechement, estInstallee, lireAbonnement } from '../lib/push';
import { DEPENDANCES } from './Reglages';

function Etape({ numero, titre, faite, children }: { numero: number; titre: string; faite: boolean; children: ReactNode }) {
  return (
    <Fenetre className={`etape-installation${faite ? ' faite' : ''}`}>
      <div className="etape-tete">
        <span className="etape-numero" aria-hidden="true">{faite ? '✓' : numero}</span>
        <span className="display" style={{ fontWeight: 500 }}>{titre}</span>
        {faite && <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>fait</span>}
      </div>
      {children}
    </Fenetre>
  );
}

export function Installer({ actions }: { actions: Actions }) {
  const plateforme = plateformeCourante();
  const iphone = estIphone(plateforme);
  const installee = estInstallee();
  const [, rafraichir] = useState(0);
  const [abo, setAbo] = useState<EtatAbonnement>({ actif: false, endpoint: null });
  const [essai, setEssai] = useState<'avant' | 'envoye' | 'pas-recu'>('avant');
  const [occupe, setOccupe] = useState(false);

  useEffect(() => surInvitation(() => rafraichir((n) => n + 1)), []);
  useEffect(() => {
    void lireAbonnement().then(setAbo);
  }, []);

  const blocage = empechement();
  // Sur ordinateur, rien à installer ; sur Android, c'est conseillé mais les notifications marchent sans.
  const installation = installee || plateforme === 'ordinateur';
  const gestes = gestesInstallation(plateforme);

  const installer = async () => {
    if (await installerDirectement()) actions.toast({ texte: 'Alfred est installé : ouvrez-le depuis son icône.' });
    rafraichir((n) => n + 1);
  };
  const activerNotifications = async () => {
    setOccupe(true);
    try {
      setAbo(await activer(DEPENDANCES));
    } catch (e) {
      const m = (e as Error).message;
      actions.toast({ texte: m === 'refuse' ? 'Notifications refusées sur cet appareil.' : `Impossible d’activer : ${m}`, erreur: true });
    } finally {
      setOccupe(false);
      rafraichir((n) => n + 1);
    }
  };
  const envoyerEssai = async () => {
    setOccupe(true);
    try {
      await api.testerPush();
      setEssai('envoye');
    } catch (e) {
      actions.toast({ texte: (e as Error).message, erreur: true });
    } finally {
      setOccupe(false);
    }
  };
  const terminer = () => {
    noterFait();
    window.location.hash = '#/';
  };
  const plusTard = () => {
    noterPlusTard();
    window.location.hash = '#/';
  };

  return (
    <main className="ecran">
      <EnTeteRetour titre="Installer Alfred" />
      <p className="message" style={{ fontSize: 14 }}>
        {iphone
          ? 'Trois minutes pour qu’Alfred vive sur votre téléphone et puisse vous prévenir.'
          : plateforme === 'ordinateur'
            ? 'Sur ordinateur, rien à installer : il reste à autoriser les notifications.'
            : 'Deux minutes pour avoir Alfred sur votre écran d’accueil, et ses notifications.'}
      </p>

      {plateforme !== 'ordinateur' && (
        <Etape numero={1} titre="Mettre Alfred sur l’écran d’accueil" faite={installee}>
          {installee ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Vous êtes dans l’app installée.</p>
          ) : installableDirectement() ? (
            <button className="btn principal large" onClick={() => void installer()}>Installer Alfred</button>
          ) : (
            <ol className="gestes">
              {gestes.map((g) => <li key={g}>{g}</li>)}
            </ol>
          )}
          {!installee && (
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Ensuite, ouvrez Alfred depuis sa nouvelle icône.
              {iphone && ' Sur iPhone, l’app installée a sa propre connexion : reconnectez-vous une fois, avec le même e-mail et le même mot de passe. Cet écran vous y attendra.'}
              {!iphone && ' Vous pouvez aussi continuer ici : les notifications marchent dans le navigateur.'}
            </p>
          )}
        </Etape>
      )}

      <Etape numero={plateforme === 'ordinateur' ? 1 : 2} titre="Autoriser les notifications" faite={abo.actif}>
        {abo.actif ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Actives sur cet appareil.</p>
        ) : blocage === 'hors-pwa-ios' ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Sur iPhone, elles s’activent dans l’app installée : faites d’abord l’étape 1, puis ouvrez Alfred depuis son icône.
          </p>
        ) : blocage === 'refuse' ? (
          <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
            Elles ont été refusées. Pour les rétablir :{' '}
            {iphone
              ? 'Réglages de l’iPhone → Notifications → Alfred → Autoriser les notifications.'
              : plateforme === 'ordinateur'
                ? 'cliquez sur le cadenas à gauche de l’adresse, puis Notifications → Autoriser.'
                : 'appui long sur l’icône d’Alfred → Infos sur l’appli → Notifications → activer.'}
            {' '}Puis revenez ici.
          </p>
        ) : blocage !== 'aucun' ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Ce navigateur ne gère pas les notifications. Essayez Chrome, Safari, Edge ou Firefox à jour.</p>
        ) : (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Le téléphone va demander l’autorisation : répondez « Autoriser ».</p>
            <button className="btn principal large" disabled={occupe} onClick={() => void activerNotifications()}>Activer les notifications</button>
          </>
        )}
      </Etape>

      <Etape numero={plateforme === 'ordinateur' ? 2 : 3} titre="Recevoir une notification d’essai" faite={false}>
        {!abo.actif ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Après l’étape précédente.</p>
        ) : essai === 'avant' ? (
          <button className="btn principal large" disabled={occupe} onClick={() => void envoyerEssai()}>M’envoyer une notification</button>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 14 }}>{essai === 'envoye' ? 'Elle est partie. Vous l’avez reçue ?' : 'Quelques pistes :'}</p>
            {essai === 'pas-recu' && (
              <ul className="gestes">
                <li>Un mode Concentration ou « Ne pas déranger » est-il actif ? Il retient les notifications.</li>
                <li>{iphone ? 'Réglages → Notifications → Alfred : « Autoriser » et « Écran verrouillé » cochés.' : 'Les notifications d’Alfred sont-elles autorisées dans les réglages du téléphone ?'}</li>
                <li>Elle peut mettre une minute. Puis renvoyez-en une.</li>
              </ul>
            )}
            <div className="boutons">
              <button className="btn principal" onClick={terminer}>Oui, reçue</button>
              {essai === 'envoye'
                ? <button className="btn" onClick={() => setEssai('pas-recu')}>Non</button>
                : <button className="btn" disabled={occupe} onClick={() => void envoyerEssai()}>Renvoyer</button>}
            </div>
          </>
        )}
      </Etape>

      <button className="btn fantome" onClick={plusTard}>Plus tard</button>
    </main>
  );
}

/**
 * Le bandeau du brief : tant qu'Alfred n'est pas installé (sur téléphone) ou que ses notifications ne sont pas actives
 * sur cet appareil, et hors de la semaine qui suit un « plus tard ».
 */
export function BandeauInstallation() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!bandeauAttendu(lireSuivi())) return;
    const telephone = plateformeCourante() !== 'ordinateur';
    void lireAbonnement().then((a) => setVisible((telephone && !estInstallee()) || !a.actif));
  }, []);

  if (!visible) return null;
  const iphone = estIphone(plateformeCourante());
  return (
    <div className="bandeau-installation">
      <a href="#/installer" style={{ color: 'inherit', flex: '1 1 auto' }}>
        <b>{plateformeCourante() === 'ordinateur' ? 'Recevoir les notifications d’Alfred' : 'Installer Alfred sur ce téléphone'}</b>
        <small className="muted">{iphone ? 'Indispensable sur iPhone pour être prévenu.' : 'Pour qu’il puisse vous prévenir.'}</small>
      </a>
      <button className="btn petit fantome" onClick={() => { noterPlusTard(); setVisible(false); }}>Plus tard</button>
    </div>
  );
}
